import { useState, useEffect } from 'react';
import { colors } from '../../../styles/colors';
import { Card, CardContent, Typography, Button, Box, Snackbar, Alert } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { useNavigate } from 'react-router-dom';
import { getPendingTransactionDetails, processTransactions } from '../../../services/transactionService';

interface PendingTransactionsCardProps {
  onProcessSuccess?: () => void;
  onProcessError?: () => void;
}

const PendingTransactionsCard = ({ onProcessSuccess, onProcessError }: PendingTransactionsCardProps) => {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [data, setData] = useState({ untriedCount: 0, failedCount: 0 });
  const [feedback, setFeedback] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const details = await getPendingTransactionDetails();
      setData(details);
    } catch (error) {
      console.error('Failed to fetch pending transaction details:', error);
    } finally {
      setLoading(false);
    }
  };

  interface ProcessError extends Error {
    successfulFiles?: number;
    failedFiles?: number;
  }

  const constructErrorMessage = (err: ProcessError) => {
    console.error('[constructErrorMessage]', err)
    let msg = err instanceof Error ? err.message : 'Failed to process transactions.'
    msg += ` ${err.successfulFiles || 0} files succeeded and ${err.failedFiles || 0} failed.`
    return msg;
  }

  const handleProcess = async () => {
    setProcessing(true);
    try {
      const response = await processTransactions();
      await fetchData();
      setFeedback({
        open: true,
        message: `${response.message}`,
        severity: 'success'
      });
      onProcessSuccess?.();
    } catch (error) {
      console.error('Failed to process transactions:', error);
      await fetchData();
      setFeedback({
        open: true,
        message: constructErrorMessage(error as ProcessError),
        severity: 'error'
      });
      onProcessError?.();
    } finally {
      setProcessing(false);
    }
  };

  const handleViewPending = () => {
    navigate('/transactions?status=Pending');
  };

  const handleViewFailed = () => {
    navigate('/transactions?status=Error');
  };

  const handleCloseFeedback = () => {
    setFeedback(prev => ({ ...prev, open: false }));
  };

  const handleViewLogs = () => {
    handleCloseFeedback();
    navigate('/output-logs');
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography>Loading...</Typography>
        </CardContent>
      </Card>
    );
  }

  const pieData = [
    { id: 0, value: data.untriedCount, label: 'Pending', color: colors.pending },
    { id: 1, value: data.failedCount, label: 'Failed', color: colors.failed }
  ];

  const totalCount = data.untriedCount + data.failedCount;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Pending Transactions
        </Typography>
        
        <Box sx={{ height: 150, display: 'flex', justifyContent: 'center', position: 'relative' }}>
          <PieChart
            series={[
              {
                data: pieData,
                //innerRadius: 0,
                highlightScope: { faded: 'global', highlighted: 'item' },
                faded: { innerRadius: 0.6, additionalRadius: -30 },
              },
            ]}
            height={150}
            width={260}
          />
        </Box>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="h4" color="primary">
            {data.untriedCount}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Pending Transactions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Failed: {data.failedCount}
          </Typography>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={handleProcess}
            disabled={processing || data.untriedCount === 0}
          >
            {processing ? 'Processing...' : 'Process Pending'}
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleViewFailed}
            disabled={data.failedCount === 0}
          >
            Resolve Failures
          </Button>
          <Button
            variant="outlined"
            onClick={handleViewPending}
            disabled={totalCount === 0}
          >
            View
          </Button>
        </Box>
      </CardContent>
      <Snackbar 
        open={feedback.open} 
        autoHideDuration={6000} 
        onClose={handleCloseFeedback}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseFeedback} 
          severity={feedback.severity}
          variant="filled"
          sx={{ width: '100%' }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={handleViewLogs}
            >
              View Logs
            </Button>
          }
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default PendingTransactionsCard;
